import React, { ReactElement, useEffect } from 'react';

import cn from 'classnames';

import styles from './Input.module.css';
import { useOrionUID } from '../../helpers/orionUID';
import {
  formatAsCurrency,
  formatAsPartialCurrency,
  sanitizePartialCurrencyValue,
  updateCursorLocation,
} from '../currencyInput/CurrencyInput.helpers';
import { Description } from '../description/Description';
import { Icon, Icons } from '../icon/Icon';
import { Label } from '../label/Label';
import { Message } from '../message/Message';

type InputType =
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'month'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

export type Currency = {
  locale: Locale;
  currencySymbol?: boolean;
  minorUnits?: boolean;
};

// Using string & {} to get enum auto-complete while allowing any string
// eslint-disable-next-line @typescript-eslint/ban-types
export type Locale = 'en-GB' | 'de-DE' | (string & {});

type InputProps = {
  label?: string | ReactElement | ReactElement[];
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  description?: string;
  descriptionProps?: React.HTMLAttributes<HTMLSpanElement>;
  iconLeft?: (typeof Icons)[number];
  iconRight?: (typeof Icons)[number];
  error?: string;
  success?: string;
  wrapperClassName?: string;
  type?: InputType | 'currency';
  currency?: Currency;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      disabled,
      iconLeft,
      iconRight,
      label,
      labelProps,
      description,
      descriptionProps,
      error,
      success,
      wrapperClassName,
      type,
      currency,
      ...inputProps
    },
    ref,
  ) => {
    useEffect(() => {
      if (type === 'currency') {
        console.warn(
          '<Input /> with type currency has been depreciated, please use <CurrencyInput /> component ',
        );
      }
    }, [type]);

    const orionUID = useOrionUID();

    const isCurrency = type === 'currency' && currency;

    const value =
      isCurrency && inputProps.value
        ? formatAsCurrency(inputProps.value.toString(), currency)
        : inputProps.value;

    const defaultValue =
      isCurrency && inputProps.defaultValue
        ? formatAsCurrency(inputProps.defaultValue.toString(), currency)
        : inputProps.defaultValue;

    const onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
      if (isCurrency) {
        const sanitisedValue = sanitizePartialCurrencyValue(
          ev.target.value,
          currency,
        );
        const formattedCurrency = formatAsPartialCurrency(
          sanitisedValue,
          currency,
        );
        ev.target.value = formattedCurrency;

        // update cursor location
        updateCursorLocation(ev, currency);
      }

      inputProps.onChange && inputProps.onChange(ev);
    };

    const onBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
      if (isCurrency) {
        const sanitisedValue = sanitizePartialCurrencyValue(
          ev.target.value,
          currency,
        );
        ev.target.value = formatAsCurrency(sanitisedValue, currency);
      }
      inputProps.onBlur && inputProps.onBlur(ev);
    };

    return (
      <div className={cn(styles.formElementWrapper, wrapperClassName)}>
        {label && (
          <Label htmlFor={inputProps.id || orionUID} {...labelProps}>
            {label}
          </Label>
        )}
        {description && (
          <Description
            className={styles.description}
            description={description}
            id={`${inputProps.id || orionUID}_desc`}
            {...descriptionProps}
          />
        )}
        <div
          className={cn(styles.inputWrapper, className, {
            [styles.disabled]: disabled,
            [styles.leftIconPadding]: !!iconLeft,
            [styles.rightIconPadding]: !!iconRight,
            [styles.topMargin]: !!label || !!description,
            [styles.error]: error,
          })}
        >
          {iconLeft && <Icon className={styles.iconLeft} icon={iconLeft} />}
          <input
            ref={ref}
            disabled={disabled}
            aria-describedby={`${inputProps.id || orionUID}_desc`}
            id={orionUID}
            type={type}
            {...inputProps}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            onBlur={onBlur}
            // ensures correct keyboard is shown on ios / android
            inputMode={isCurrency ? 'decimal' : inputProps.inputMode}
          />
          {iconRight && <Icon className={styles.iconRight} icon={iconRight} />}
        </div>
        <div className={styles.status}>
          {error && <Message message={error} type="error" />}
          {success && <Message message={success} type="success" />}
        </div>
      </div>
    );
  },
);

Input.displayName = 'Input';
